'use client'

import { useEffect, useMemo, useRef } from 'react'
import { cn, toast } from '@heroui/react'

import { Icon } from '@/components/ui/icon'
import type { SilhouetteKind } from '@/lib/quote/vehicle-silhouette'
import {
  QUOTE_PHOTO_ANGLES,
  QUOTE_PHOTO_LIMITS,
  type QuotePhotoAngle,
} from '@/lib/validation/input/inquiry-schema'

import { VehicleSilhouette } from './vehicle-silhouettes'

export type QuotePhotos = Partial<Record<QuotePhotoAngle, File>>

interface VehiclePhotoCardsProps {
  kind: SilhouetteKind
  photos: QuotePhotos
  onChange: (next: QuotePhotos) => void
}

const ACCEPT = 'image/*'

/**
 * Four photo slots, one per angle the estimator wants, each showing an
 * outline of the customer's body type from that angle until a photo fills
 * it. The input accepts any image with no `capture` attribute on purpose:
 * on a phone that opens the native sheet offering the camera AND the
 * library, so someone who already shot the car earlier can pick from it.
 * `capture` would force the camera and hide the library.
 */
export function VehiclePhotoCards({ kind, photos, onChange }: VehiclePhotoCardsProps) {
  return (
    <div className='space-y-2'>
      <span className='block text-sm font-medium text-foreground'>
        Photos of the vehicle <span className='font-normal text-muted'>(optional, but they speed up the quote)</span>
      </span>
      <ul className='grid grid-cols-2 gap-3 sm:grid-cols-4' aria-label='Vehicle photos'>
        {QUOTE_PHOTO_ANGLES.map((angle) => (
          <PhotoCard
            key={angle.key}
            angleKey={angle.key}
            label={angle.label}
            hint={angle.hint}
            kind={kind}
            file={photos[angle.key]}
            onPick={(file) => {
              const next = { ...photos }
              if (file) next[angle.key] = file
              else delete next[angle.key]
              onChange(next)
            }}
          />
        ))}
      </ul>
    </div>
  )
}

function PhotoCard({
  angleKey,
  label,
  hint,
  kind,
  file,
  onPick,
}: {
  angleKey: QuotePhotoAngle
  label: string
  hint: string
  kind: SilhouetteKind
  file?: File
  onPick: (file: File | null) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file])
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const pick = (list: FileList | null) => {
    const f = list?.[0]
    if (inputRef.current) inputRef.current.value = ''
    if (!f) return
    if (f.size > QUOTE_PHOTO_LIMITS.maxBytes) {
      toast.danger('Photo too large', {
        description: `${label}: keep each photo under ${QUOTE_PHOTO_LIMITS.maxBytes / (1024 * 1024)} MB.`,
      })
      return
    }
    onPick(f)
  }

  return (
    <li className='relative'>
      <label
        className={cn(
          'flex aspect-[4/3] cursor-pointer flex-col overflow-hidden rounded-xl border-2 bg-background text-center transition-colors',
          file ? 'border-border' : 'border-dashed border-border hover:border-foreground/40'
        )}
      >
        {previewUrl ? (
          // Native img: the source is a blob URL for a file that never leaves
          // the browser until submit, so next/image has nothing to optimise.
          <img
            src={previewUrl}
            alt={`${label} photo selected`}
            className='h-full w-full object-cover'
            onError={(e) => {
              // HEIC previews fail outside Safari; keep the card usable.
              e.currentTarget.style.display = 'none'
            }}
          />
        ) : (
          <>
            <div className='flex flex-1 items-center justify-center px-3 pt-2 text-muted'>
              <VehicleSilhouette kind={kind} angle={angleKey} />
            </div>
            <div className='flex items-center justify-center gap-1 pb-2 text-xs font-semibold text-foreground'>
              <Icon icon='camera' size='sm' aria-hidden='true' />
              Add photo
            </div>
          </>
        )}
        <input
          ref={inputRef}
          type='file'
          accept={ACCEPT}
          className='sr-only'
          onChange={(e) => pick(e.target.files)}
          aria-label={`Add ${label.toLowerCase()} photo`}
        />
      </label>
      <div className='mt-1 flex items-start justify-between gap-1'>
        <div className='min-w-0'>
          <p className='text-xs font-semibold text-foreground'>{label}</p>
          <p className='truncate text-[11px] text-muted'>{file ? file.name : hint}</p>
        </div>
        {file && (
          <button
            type='button'
            aria-label={`Remove ${label.toLowerCase()} photo`}
            onClick={() => onPick(null)}
            className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted hover:bg-surface hover:text-foreground'
          >
            <Icon icon='cw-close' size='sm' aria-hidden='true' />
          </button>
        )}
      </div>
    </li>
  )
}
